import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogConfig } from '@angular/material/dialog';
import { MediaObserver } from '@ngbracket/ngx-layout';
import { filter, map, takeUntil } from 'rxjs/operators';

import { LazyDialogLoader } from '@qtek/libs/dialog-loader';
import { MetaStore, RelationsStore } from '@qtek/libs/store';
import {
  Customer,
  DocumentReferenceEntityType,
  PhoneType,
  Staff,
} from '@qtek/shared/models';
import { Observable, Subject } from 'rxjs';

export const contactPreviewDialogConfig: MatDialogConfig = {
  panelClass: 'fullscreen',
  autoFocus: false,
};

export interface ContactPrevievDialogInput extends Staff {
  type: 'staff' | 'customer';
}

@Component({
  selector: 'qt-contact-preview-dialog',
  templateUrl: './contact-preview-dialog.component.html',
  styleUrls: ['./contact-preview-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ContactPreviewDialogComponent implements OnDestroy {
  private readonly data = inject<ContactPrevievDialogInput>(MAT_DIALOG_DATA);
  protected readonly media = inject(MediaObserver);
  private metaStore = inject(MetaStore);
  private relationsStore = inject(RelationsStore);
  private dialogLoader = inject(LazyDialogLoader);
  /** Unused — this avatar shows initials only, no image id is bound. */
  protected readonly personReferenceType = DocumentReferenceEntityType.PERSON;

  contact = signal<Customer | Staff | null>(null);

  protected readonly personData = computed(
    () =>
      (this.contact() as Customer)?.person ??
      (this.contact() as Staff)?.personApi
  );
  protected readonly companyData = computed(
    () =>
      (this.contact() as Customer)?.company ?? (this.contact() as Staff)?.acn
  );
  protected readonly firstName = computed(
    () =>
      (this.contact() as Customer)?.person?.fname ??
      (this.contact() as Staff)?.personApi?.firstName
  );
  protected readonly statusCode = computed(
    () => (this.contact() as Staff)?.status
  );

  protected readonly avatarReferenceId = computed(
    () =>
      (this.contact() as Customer)?.person?.avatarDocId ??
      (this.contact() as Staff)?.personApi?.avatarDocId
  );

  type: 'staff' | 'customer';
  destroy$ = new Subject<void>();

  protected readonly role = computed(() => {
    const r = this.metaStore
      .allRoles()
      .find(role => role.id === this.data.role?.toString());
    return r ? r.name : '';
  });

  get relation(): string {
    switch (this.type) {
      case 'customer':
        return 'PTL_GRP_CUSTOMER';
      case 'staff':
        return 'GNR_STAFF';
      default:
        return '';
    }
  }

  constructor() {
    this.type = this.data.type;

    const relations$: Observable<Staff[] | Customer[]> =
      this.type === 'staff'
        ? toObservable(this.relationsStore.staff)
        : toObservable(this.relationsStore.customers);

    relations$
      .pipe(
        map(relations =>
          this.type === 'staff'
            ? (relations as Staff[]).find(
                staff => staff.personId === this.data.personId
              )
            : (relations as Customer[]).find(
                customer => customer._id === this.data._id
              )
        ),
        takeUntil(this.destroy$),
        filter(Boolean)
      )
      .subscribe(contact => {
        this.contact.set(contact);
      });
  }

  protected readonly PhoneType = PhoneType;

  onEdit(): void {
    if (this.type === 'staff') {
      this.dialogLoader.open('employee-dialog', this.contact()).subscribe();
    } else {
      this.dialogLoader
        .open('customer-dialog', { customer: this.contact() })
        .subscribe();
    }
  }

  onInvite(): void {
    if (this.type === 'staff') {
      this.dialogLoader
        .open('invite-contact', {
          type: 'staff',
          relId: this.contact()._id,
          person: (this.contact() as Staff).personApi,
        })
        .subscribe();
    } else {
      this.dialogLoader
        .open('invite-contact', {
          type: 'customer',
          relId: this.contact()._id,
          person: (this.contact() as Customer).person,
          customer: this.contact(),
        })
        .subscribe();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
